import moment from 'moment/moment'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { BaseRecord } from '../../models/BaseRecord'
import { Gift } from '../../models/Gift'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import { GiftView } from './GiftView'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { NewGiftPage } from './NewGiftPage'
import { NewGiftCompletePage } from './NewGiftCompletePage'

const viewWillAppear = async ({
    owner,
    urlParams,
    props: { action },
}: PocketComponent<{ action: 'new' | 'edit' | 'copy' }>) => {
    const giftItems = await RecordFactory.getInstance().loadItems(owner.id ?? '', 'gift')

    const defaultGift = await (async (): Promise<Gift | null> => {
        switch (action) {
            case 'new': {
                return new Gift({
                    created_date: moment(),
                    taxcode: owner.taxcode,
                    tax: owner.taxcode?.rate ?? 0,
                    allow_surcharge: owner.allowSurcharge,
                    channel: owner.channel,
                })
            }
            case 'edit': {
                if (!urlParams.id) {
                    return null
                }
                return (await RecordFactory.getInstance().loadGift(urlParams.id)) ?? null
            }
            case 'copy': {
                if (!urlParams.id) {
                    return null
                }
                const gift = await RecordFactory.getInstance().loadGift(urlParams.id)
                if (!gift) {
                    return null
                }
                return gift.clone({ id: undefined })
            }
        }
    })()
    if (!defaultGift) {
        return new RecordNotFoundError()
    }

    return { defaultGift, giftItems }
}

const NewGiftWorkflowRoot = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { defaultGift, giftItems } = data

    const [searchParams, setSearchParams] = useSearchParams()

    const stepIndex = useMemo(() => {
        switch (searchParams.get('step')) {
            case 'review':
                return 1
            case 'success':
                return 2
            default:
                return 0
        }
    }, [searchParams])

    const isEditing = defaultGift.id !== undefined

    usePageTitle(isEditing ? 'Edit Gift Certificate' : 'Create Gift Certificate')

    const navigate = useNavigate()

    const [gift, setGift] = useState(defaultGift)

    const [loadingButton, setLoadingButton] = useState<'save' | 'send' | 'charge' | null>(null)

    const canAdvanceToStep2 = gift.client !== undefined && gift.item !== undefined

    WorkflowPage.useNavigationHandlers({
        stepIndex,
        isEditing,
        canAdvanceToStep2,
        recordID: gift.id,
    })

    const hasUnsavedChanges = stepIndex <= 1 && BaseRecord.hasChanges(data.defaultGift, gift)

    const onSave = async (shouldCharge: boolean) => {
        if (gift.item === undefined || gift.client === undefined) {
            return
        }

        setLoadingButton(shouldCharge ? 'charge' : 'send')

        const result = await RecordFactory.getInstance().sendGift(gift, gift.channel === '')
        setLoadingButton(null)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        setGift(result.value)

        if (shouldCharge) {
            navigate(`/charge/gift/${result.value.id}`)
        }
        else {
            setSearchParams({ step: 'success' }, { replace: true })
        }
    }

    return (
        <>
            <WorkflowPage
                title={
                    stepIndex === 0
                        ? 'Sell gift certificate'
                        : stepIndex === 1
                          ? 'Review'
                          : undefined
                }
                backButtonTitle={
                    stepIndex === 1
                        ? isEditing
                            ? 'Edit Gift Certificate'
                            : 'Create Gift Certificate'
                        : undefined
                }
                onBack={() => navigate(-1)}
                steps={[isEditing ? 'Edit' : 'Create', 'Review', 'Save']}
                currentStepIndex={stepIndex}
                shouldForceCurrentStepCheckmark={stepIndex === 2}
                onClose={{
                    action: 'go-back',
                    excludePathname: /^\/gifts.*\/(new|edit|copy)/,
                    defaultPathname: '/dashboards/gifts',
                }}
                footerContent={
                    stepIndex === 0 || stepIndex === 1 ? (
                        <div className="flex w-full items-center justify-end gap-x-4">
                            {stepIndex === 0 ? (
                                <Button
                                    intent="primary"
                                    disabled={!canAdvanceToStep2}
                                    onClick={() => setSearchParams({ step: 'review' })}
                                >
                                    Next
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        intent="secondary"
                                        onClick={() => onSave(true)}
                                        isLoading={loadingButton === 'charge'}
                                    >
                                        {gift.chargeAmount === 0 ? 'Mark Paid' : 'Charge'}
                                    </Button>
                                    {!!gift.channel && (
                                        <Button
                                            intent="primary"
                                            onClick={() => onSave(false)}
                                            isLoading={loadingButton === 'send'}
                                        >
                                            Send
                                        </Button>
                                    )}
                                    <Button
                                        intent="primary"
                                        onClick={() => onSave(false)}
                                        isLoading={loadingButton === 'save'}
                                    >
                                        Save
                                    </Button>
                                </>
                            )}
                        </div>
                    ) : undefined
                }
                shouldVerticallyCenterContent={stepIndex === 2}
            >
                {stepIndex === 0 ? (
                    <NewGiftPage
                        gift={gift}
                        onUpdateGift={setGift}
                        owner={owner}
                        giftItems={giftItems}
                    />
                ) : stepIndex === 1 && gift.client !== undefined && gift.item !== undefined ? (
                    <GiftView
                        gift={gift}
                        isWorkflowReviewStep
                        client={gift.client}
                        giftItem={gift.item}
                    />
                ) : stepIndex === 2 && gift !== null ? (
                    <NewGiftCompletePage gift={gift} />
                ) : null}
            </WorkflowPage>

            <WorkflowPage.NavigationBlocker
                when={hasUnsavedChanges}
                allowNextPaths={[/charge\/gift\/.*/]}
            />
        </>
    )
}

const render = (props: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    return (
        <WorkflowPage.ResetContainer>
            <NewGiftWorkflowRoot {...props} />
        </WorkflowPage.ResetContainer>
    )
}

export const GiftWorkflow = usePSComponent(render, viewWillAppear)
